import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Thumbs, Autoplay } from "swiper/modules";
import axios from "axios";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";

import "./ProductDetails.css";
import { ConfigContext } from "../Context/ConfigContext";

function ProductDetails() {
  const { slug } = useParams();
  const { ApiUrl, apiHeaderJson, createSlug } = useContext(ConfigContext);
  const navigate = useNavigate();

  // State for product data
  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeThumb, setActiveThumb] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);

  // Extract product ID from slug (last segment after '-')
  const productId = slug ? slug.split("-").pop() : null;



  useEffect(() => {
    if (!productId) {
      setError("Invalid product ID");
      setLoading(false);
      return;
    }

    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${ApiUrl}apis/GetProductDetails`, {
          params: { product_id: productId },
          headers: apiHeaderJson,
        });

        const { success, data, images: imgData, features: featData, related_products } = response.data;

        if (success && data && data.length > 0) {
          const productData = data[0];
          setProduct(productData);
          setImages(imgData || []);
          setFeatures(featData || []);
          setRelatedProducts(related_products || []);
          setError(null);
        } else {
          setError("Product not found");
          setRelatedProducts([]);
          setProduct(null);
          setImages([]);
          setFeatures([]);
        }
      } catch (err) {
        console.error("Failed to fetch product details:", err);
        setError(err.message || "Failed to load product");
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId, ApiUrl, apiHeaderJson]);

  // Handle loading state
  if (loading) {
    return (
      <div className="product-details-page">
        <div className="product-loading" style={{ textAlign: "center", padding: "50px" }}>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  // Handle error or no product
  if (error || !product || product.product_publish_status === 0) {
    return (
      <div className="product-details-page">
        <div className="product-not-found">
          <i className="ri-error-warning-line"></i>
          <h2>{error || "Product Not Found"}</h2>
          <p>The product you're looking for doesn't exist or an error occurred.</p>
          <button onClick={() => navigate(-1)} className="back-to-products-btn">
            <i className="ri-arrow-left-line"></i>
          </button>
        </div>
      </div>
    );
  }

  // Build main image URL and gallery images
  const mainImage = product.product_image_url || "";
  const galleryImages = images.map((img) => img.product_image_url).filter(Boolean);
  const allImages = mainImage ? [mainImage, ...galleryImages] : galleryImages;

  // Build product display object
  const displayProduct = {
    id: product.product_id,
    name: product.product_title,
    category: product.category || "Products",
    longDescription: product.product_specification || product.product_short_description || "",
    image: mainImage,
    images: allImages,
    badge: null,
    benefits: features.map((f) => f.feature).filter(Boolean),
  };


  return (
    <div className="product-details-page">
      {/* Breadcrumb */}
      <div className="product-breadcrumb">
        <div className="product-breadcrumb-container" data-aos="fade-up">
          <Link to="/">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/products">Products</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span>{displayProduct.name}</span>
        </div>
      </div>

      {/* Main Product Section */}
      <section className="product-detail-section">
        <div className="product-detail-container">
          {/* Image Slider */}
          <div className="product-detail-image-slider" data-aos="fade-right">
            {displayProduct.badge && (
              <span className="product-detail-badge">{displayProduct.badge}</span>
            )}

            <div className="product-swiper-main">
              <Swiper
                modules={[Navigation, Pagination, Thumbs, Autoplay]}
                navigation={{
                  nextEl: ".product-swiper-button-next",
                  prevEl: ".product-swiper-button-prev",
                }}
                pagination={{ clickable: true }}
                thumbs={{ swiper: activeThumb && !activeThumb.destroyed ? activeThumb : null }}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                loop={true}
                className="productMainSwiper"
              >
                {displayProduct.images.map((img, idx) => (
                  <SwiperSlide key={idx}>
                    <img src={img} alt={`${displayProduct.name} - ${idx + 1}`} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Thumbnails */}
            {displayProduct.images.length > 1 && (
              <div className="product-swiper-thumbs">
                <Swiper
                  modules={[Thumbs]}
                  onSwiper={(swiper) => setActiveThumb(swiper)}
                  slidesPerView={Math.min(displayProduct.images.length, 4)}
                  spaceBetween={10}
                  watchSlidesProgress={true}
                  className="productThumbsSwiper"
                >
                  {displayProduct.images.map((img, idx) => (
                    <SwiperSlide key={idx}>
                      <img src={img} alt={`${displayProduct.name} thumbnail ${idx + 1}`} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}

            {/* Custom Arrows */}
            <button
              className="product-swiper-button-prev product-slider-arrow product-slider-arrow-prev"
              aria-label="Previous image"
            >
              ❮
            </button>
            <button
              className="product-swiper-button-next product-slider-arrow product-slider-arrow-next"
              aria-label="Next image"
            >
              ❯
            </button>
          </div>

          {/* Info */}
          <div className="product-detail-info mt-5" data-aos="fade-left">
            <span className="product-detail-category">{displayProduct.category}</span>
            <h1 className="mt-3">{displayProduct.name}</h1>

            <p className="product-detail-description my-5">{displayProduct.longDescription}</p>

            <div className="product-detail-actions">
              <Link to={`/inquiry/${createSlug(displayProduct.name)}-${displayProduct.id}`} className="inquiry-now-btn">
                <i className="ri-chat-3-line"></i> Send Inquiry
              </Link>
            </div>

            {displayProduct.benefits && displayProduct.benefits.length > 0 && (
              <div className="product-benefits">
                {displayProduct.benefits.map((benefit, idx) => (
                  <div className="benefit-item" key={idx}>
                    <i className="ri-checkbox-circle-fill"></i>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>



      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="related-products-section">
          <div className="related-products-container">
            <h2 data-aos="fade-up">Related <span>Products</span></h2>
            <div className="related-products-grid">
              {relatedProducts.map((related, index) => (
                <Link
                  to={`/product/${createSlug(related.product_title)}-${related.product_id}`}
                  key={related.product_id}
                  className="related-product-card"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="related-product-image">
                    <img src={related.product_image_url} alt={related.product_title} loading="lazy" />
                  </div>
                  <div className=" related-product-info">
                    <h4>{related.product_title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default ProductDetails;